import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Container,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Grid,
  TextField,
  Divider,
} from "@mui/material";
import TypeFormDialog from "../sections/product-types/TypeFormDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import ConfirmDialog from "../components/ConfirmDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;

export default function ProductTypesPage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRowId, setDialogRowId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);


  const [filters, setFilters] = useState({
    name: "",
    level: "",
    parentTypeId: "",
    parentSubtypeId: "",
    isActive: "",
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const defaultFilter = {
    name: "",
    level: "",
    parentTypeId: "",
    parentSubtypeId: "",
    isActive: "",
  };

  const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleToggleStatus = async () => {
    if (!selectedItem) return;
    try {
      const newStatus = !selectedItem.isActive;
      const res = await fetchWithInterceptor(
        `/api/product-types/${selectedItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed updating status");
      }
      appContext.showMessage(
        newStatus ? "Item activated" : "Item deactivated",
        "success"
      );
      handleMenuClose();
      loadItems(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error updating status", "error");
    }
  };

  const handleToggleStatusClick = () => setConfirmOpen(true);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setDialogRowId(item.id);
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    try {
      const method = dialogRowId ? "PUT" : "POST";
      const url = dialogRowId
        ? `/api/product-types/${dialogRowId}`
        : "/api/product-types";
      const res = await fetchWithInterceptor(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed saving");
      }
      appContext.showMessage(
        dialogRowId ? "Updated successfully" : "Created successfully",
        "success"
      );
      setDialogOpen(false);
      loadItems(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving", "error");
    }
  };

  const loadItems = async (reset = false) => {
    if (loading) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.level) params.append("level", filters.level);
      if (filters.parentTypeId) params.append("parentTypeId", filters.parentTypeId);
      if (filters.parentSubtypeId) params.append("parentSubtypeId", filters.parentSubtypeId);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);
      params.append("limit", PAGE_SIZE);
      params.append("offset", reset ? 0 : paginationModel.page * PAGE_SIZE);

      const response = await fetchWithInterceptor(
        `/api/product-types?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setItems(data.rows || []);
      setRowCount(data.total || 0);
      if (reset) setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading product types", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [typesRes, subtypesRes] = await Promise.all([
        fetchWithInterceptor("/api/product-types?level=type&limit=500", {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }),
        fetchWithInterceptor("/api/product-types?level=subtype&limit=500", {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }),
      ]);
      if (typesRes.ok) {
        const d = await typesRes.json();
        const list = d.rows ?? d.data ?? [];
        setTypes(Array.isArray(list) ? list : []);
      }
      if (subtypesRes.ok) {
        const d = await subtypesRes.json();
        const list = d.rows ?? d.data ?? [];
        setSubtypes(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadItems(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadItems(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = [
    {
      field: "level",
      headerName: "LEVEL",
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={params.row.level?.toUpperCase()} size="small" />
      ),
    },
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "parentType",
      headerName: "TYPE",
      flex: 1,
      renderCell: (params) => params.row.parentType?.name || "-",
    },
    {
      field: "parentSubtype",
      headerName: "SUBTYPE",
      flex: 1,
      renderCell: (params) => params.row.parentSubtype?.name || "-",
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Button
            size="small"
            variant="text"
            sx={{ mr: 1, minWidth: "auto", p: 0.5 }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate our tree of types, subtypes, and sub-subtypes. Changes made here will be consumed by other modules in the ZYN web app (e.g., product creation).
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{ width: "100%", backgroundColor: "background.paper", mt: 2, mb: 2 }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Level"
                  name="level"
                  variant="outlined"
                  fullWidth
                  value={filters.level}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="type">Type</MenuItem>
                  <MenuItem value="subtype">Subtype</MenuItem>
                  <MenuItem value="subsubtype">Subsubtype</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Type"
                  name="parentTypeId"
                  variant="outlined"
                  fullWidth
                  value={filters.parentTypeId === "" ? "" : String(filters.parentTypeId)}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL TYPES</MenuItem>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Subtype"
                  name="parentSubtypeId"
                  variant="outlined"
                  fullWidth
                  value={filters.parentSubtypeId === "" ? "" : String(filters.parentSubtypeId)}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL SUBTYPES</MenuItem>
                  {subtypes.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Status"
                  name="isActive"
                  variant="outlined"
                  fullWidth
                  value={filters.isActive}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setDialogRowId(null);
                setSelectedItem(null);
                setDialogOpen(true);
              }}
            >
              CREATE
            </Button>
          </Grid>

          <DataGrid
            rows={items}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
            }}
          />
        </Paper>

        <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
          <MenuItem onClick={handleToggleStatusClick}>
            {selectedItem?.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
        </Menu>

        <TypeFormDialog
          open={dialogOpen}
          rowId={dialogRowId}
          productType={selectedItem}
          onClose={() => {
            setDialogOpen(false);
            setDialogRowId(null);
            setSelectedItem(null);
          }}
          onSave={handleSave}
        />

        <ConfirmDialog
          open={confirmOpen}
          title={
            selectedItem?.isActive
              ? "Deactivate"
              : "Activate"
          }
          message={
            selectedItem?.isActive ? (
              <>
                Are you sure you want to deactivate
                <strong style={{ fontWeight: 500 }}> "{selectedItem?.name}"?</strong>
              </>
            ) : (
              <>
                Are you sure you want to activate
                <strong style={{ fontWeight: 500 }}> "{selectedItem?.name}"?</strong>
              </>
            )
          }
          onCancel={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            await handleToggleStatus();
          }}
        />
      </Container>
    </>
  );
}